import type { AlertColor } from '@mui/material';
import {
  IconButton,
  Popover,
  Grid,
  Box,
  Link,
  Typography,
  Divider,
  List,
  ListItem as MuiListItem,
  styled,
  Alert as MuiAlert,
  AlertTitle,
  Tooltip,
  Badge,
  Button,
} from '@mui/material';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useCallback, useMemo, useState } from 'react';
import { Bell } from 'react-feather';
import { Close, Info, Call, CallEnd, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { VehicleAlert } from '@data/notification/vehicle/types';
import {
  useVehicleAlertContent,
  vehicleAlertListAtom,
} from '@data/notification/vehicle';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Stack } from '@mui/system';
import { getDriveLink } from '@components/common/DriveNotification/utils';
import { remoteMonitoringSettingAtom } from '@data/fms/environment/states';

type AlertItemProps = {
  no: number;
  alert: VehicleAlert;
};

const AlertItem: React.FC<AlertItemProps> = ({ no, alert }: AlertItemProps) => {
  const setVehicleAlertList = useSetAtom(vehicleAlertListAtom);
  const { t } = useTranslation();
  const { title, level, message } = useVehicleAlertContent(alert);
  const remoteMonitoringSettings = useAtomValue(remoteMonitoringSettingAtom);

  const severity: AlertColor =
    level === 'receiveCall'
      ? 'warning'
      : level === 'hangupCall'
      ? 'info'
      : (level as AlertColor);

  const showMessage = useMemo(() => alert.type !== 'receiveCall', [alert.type]);

  const showDriveLink = useMemo(
    () =>
      !!remoteMonitoringSettings?.value.notification &&
      (alert.type === 'telemetryArrivalErrorAlarm' ||
        alert.type === 'receiveCall' ||
        alert.type === 'hangupCall' ||
        alert.type === 'cameraQualityDeterioration' ||
        alert.type === 'audioQualityDeterioration' ||
        alert.type === 'rtc' ||
        alert.type === 'mrm' ||
        alert.type === 'heartbeatRejected' ||
        alert.type === 'heartbeatDisconnected'),
    [alert.type, remoteMonitoringSettings?.value.notification],
  );

  const driveLinkButtonColor = useMemo(
    () =>
      level === 'receiveCall'
        ? 'warning'
        : level === 'hangupCall'
        ? 'info'
        : (level as 'error' | 'success' | 'warning' | 'info'),
    [level],
  );

  const icon =
    level === 'receiveCall' ? (
      <Call fontSize="inherit" />
    ) : level === 'hangupCall' ? (
      <CallEnd fontSize="inherit" />
    ) : undefined;

  /**
   * Close ボタンクリック
   */
  const handleClickCloseButton = useCallback(() => {
    setVehicleAlertList((prevState) => prevState.filter((_, i) => i !== no));
  }, [no, setVehicleAlertList]);

  return (
    <ListItem disableGutters>
      <MuiAlert
        severity={severity}
        icon={icon}
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={handleClickCloseButton}
          >
            <Close fontSize="inherit" data-testid="CloseIcon" />{' '}
          </IconButton>
        }
      >
        <AlertTitle>{title}</AlertTitle>
        <Typography variant="caption">{alert.notifiedAt}</Typography>
        <Stack spacing={4} direction="row">
          <Typography>{alert.vehicle.vehicle_name}</Typography>
          {alert.driveOptions?.deviceTag && (
            <Typography>
              {t('notification.drive.device_name')}:{' '}
              {alert.driveOptions.deviceTag}
            </Typography>
          )}
        </Stack>
        {showMessage && message}
        {showDriveLink && (
          <Box mt={2}>
            <Button
              href={getDriveLink(alert.vehicle)}
              size="small"
              variant="outlined"
              color={driveLinkButtonColor}
              target="_blank"
              rel="noreferrer noopener"
              endIcon={<OpenInNew />}
              data-testid="snackbar-link-button"
            >
              {t('notification.drive.link')}
            </Button>
          </Box>
        )}
      </MuiAlert>
    </ListItem>
  );
};

const ListItem = styled(MuiListItem)`
  .MuiAlert-root {
    width: 100%;
  }

  &:last-of-type {
    padding-bottom: 0;
  }
`;

const HeaderVehicleAlertList: React.FC = () => {
  const [vehicleAlertList, setVehicleAlertList] = useAtom(vehicleAlertListAtom);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();

  /**
   * ベルアイコンボタンクリック
   */
  const handleClickIconButton = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget);
    },
    [],
  );

  /**
   * Popover を閉じる
   */
  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
  }, []);

  /**
   * すべてクリアするボタンクリック
   */
  const handleClickAllClearButton = useCallback(() => {
    setVehicleAlertList([]);
  }, [setVehicleAlertList]);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClickIconButton}
        data-testid="alertInfo-icon"
      >
        <Badge
          variant="dot"
          color="error"
          invisible={vehicleAlertList.length === 0}
        >
          <Bell />
        </Badge>
      </IconButton>
      <Popover
        open={!isNullOrUndefined(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box width={440} p={4} minHeight={200} maxHeight={600}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid>
              <Grid container spacing={2}>
                <Grid>
                  <Typography variant="h6">
                    {t('notification.vehicle_alert_list.title')}
                  </Typography>
                </Grid>
                <Grid>
                  <Tooltip
                    arrow
                    title={t('notification.vehicle_alert_list.tooltip')}
                  >
                    <span>
                      <Info fontSize="small" color="info" />
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
            {vehicleAlertList.length > 0 && (
              <Grid>
                <Link
                  component="button"
                  color="textSecondary"
                  onClick={handleClickAllClearButton}
                >
                  {t('notification.vehicle_alert_list.clear_all')}
                </Link>
              </Grid>
            )}
          </Grid>
          <Box mt={2} mb={2}>
            <Divider />
          </Box>
          <Box>
            {vehicleAlertList.length === 0 ? (
              <Box mt={14}>
                <Typography textAlign="center" color="GrayText">
                  {t('notification.vehicle_alert_list.none')}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {vehicleAlertList.map((alert, i) => (
                  <AlertItem key={`alert_${i}`} no={i} alert={alert} />
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default HeaderVehicleAlertList;
